import GoogleMap, { ICoordinate, SinglePointCoordinateData } from "components/GoogleMap/googlemap";
import { Loading } from "components/panels";
import { groupPlanningItemsByLocation, isAirportItem, isShowItem, isVenueItem, PlanningItem, planningItemIsOnLocation } from "Planning/planningMapHelpers";
import React, { forwardRef, useImperativeHandle } from "react";
import { bounce, bounceOnce, useHoverMarker, useSelectMarker } from "utilities/googlemaps";
import { useLocalStorage } from "utilities/usehooks";

interface Props {
  isLoading: boolean

  shows: server.dto.ShowForPlanningMap[]
  venues: server.dto.VenueForPlanningMap[]
  airports: server.dto.AirportForPlanningMap[]

  selectedLocation: server.dto.Geo
  onSelectedLocationChange: (location: server.dto.Geo) => void;

  hoveredLocation: server.dto.Geo
  onHoveredLocationChange: (location: server.dto.Geo) => void;

  onBoundsChange: (bounds: google.maps.LatLngBounds) => void;

  onMarkerClicked: (item: PlanningItem[]) => void;
}

export interface GoogleMapPlanningRef {
  zoomToFit: () => void;
  bounceMarkerOnce: (location: server.dto.Geo) => void;
}

export const GoogleMapPlanning = forwardRef((props: Props, ref) => {
  const { isLoading, shows, venues, airports } = props;
  const selectMarker = useSelectMarker();
  const onMarkerHover = useHoverMarker(props.selectedLocation);
  const [zoom, setZoom] = useLocalStorage("planning:map:zoom-level", 2);
  const [center, setCenter] = useLocalStorage("planning:map:center", { lat: 0, lng: 0 });

  const mapRef = React.useRef<GoogleMap>(null);

  const bounceMarkerOnce = (location: server.dto.Geo) => {
    const marker = mapRef.current.getMarkerFromCoordinate({
      position: { lat: location.lat, lng: location.lng },
    });

    bounceOnce(marker);
  }

  useImperativeHandle(ref, () => ({
    zoomToFit: () => {
      mapRef.current.zoomToFit();
    },
    bounceMarkerOnce
  }));

  const groupedItemsByLocation = groupPlanningItemsByLocation([...shows, ...venues, ...airports]);

  React.useEffect(() => {
    if (props.selectedLocation) {
      const markerThatShouldBeSelected = mapRef.current.getMarkerFromCoordinate({
        position: { lat: props.selectedLocation.lat, lng: props.selectedLocation.lng },
      });

      selectMarker(markerThatShouldBeSelected);
      bounce(markerThatShouldBeSelected);
    }
  }, [props.selectedLocation])

  React.useEffect(() => {
    let hoveredGoogleMarker: google.maps.marker.AdvancedMarkerElement = undefined;
    if (props.hoveredLocation) {
      hoveredGoogleMarker = mapRef.current.getMarkerFromCoordinate({
        position: { lat: props.hoveredLocation.lat, lng: props.hoveredLocation.lng },
      });
    }

    onMarkerHover(hoveredGoogleMarker);
  }, [props.hoveredLocation]);

  const coordinates: ICoordinate[] = Object.values(groupedItemsByLocation).map<ICoordinate>(groupedItems => {
    if (groupedItems.length === 1) {
      const [item] = groupedItems;
      if (isShowItem(item)) {
        return {
          clickable: true,
          position: { lat: item.point.lat, lng: item.point.lng },
          data: SinglePointCoordinateData,
          pin: {
            type: "show",
            phase: item.phase
          },

          onLoaded: (_, marker) => {
            if (props.selectedLocation && planningItemIsOnLocation(item, props.selectedLocation)) {
              selectMarker(marker);
            }

            google.maps.event.addListener(marker, "click", () => {
              props.onMarkerClicked([item]);
              bounce(marker);
              props.onSelectedLocationChange(item.point);
            });

            marker.content.addEventListener("mouseenter", () => {
              props.onHoveredLocationChange(item.point);
            });

            marker.content.addEventListener("mouseleave", () => {
              props.onHoveredLocationChange(undefined);
            });
          },
        }
      } else if (isVenueItem(item)) {
        return {
          position: { lat: item.point.lat, lng: item.point.lng },
          data: SinglePointCoordinateData,
          pin: {
            type: "venue",
          },
          onLoaded: (_, marker) => {
            if (props.selectedLocation && planningItemIsOnLocation(item, props.selectedLocation)) {
              selectMarker(marker);
            }
            google.maps.event.addListener(marker, "click", () => {
              props.onMarkerClicked([item]);
              bounce(marker);
              props.onSelectedLocationChange(item.point);
            });

            marker.content.addEventListener("mouseenter", () => {
              props.onHoveredLocationChange(item.point);
            });

            marker.content.addEventListener("mouseleave", () => {
              props.onHoveredLocationChange(undefined);
            });
          },
        }
      }
      else if (isAirportItem(item)) {
        return {
          position: { lat: item.point.lat, lng: item.point.lng },
          data: SinglePointCoordinateData,
          pin: {
            type: "airport",
          },
          onLoaded: (_, marker) => {
            if (props.selectedLocation && planningItemIsOnLocation(item, props.selectedLocation)) {
              selectMarker(marker);
            }
            google.maps.event.addListener(marker, "click", () => {
              props.onMarkerClicked([item]);
              bounce(marker);
              props.onSelectedLocationChange(item.point);
            });

            marker.content.addEventListener("mouseenter", () => {
              props.onHoveredLocationChange(item.point);
            });

            marker.content.addEventListener("mouseleave", () => {
              props.onHoveredLocationChange(undefined);
            });
          },
        }
      }
    } else {
      return {
        position: { lat: groupedItems[0].point.lat, lng: groupedItems[0].point.lng },
        data: {
          entryCount: groupedItems.length
        },
        pin: {
          type: "multiple",
          count: groupedItems.length
        },
        onLoaded: (_, marker) => {
          if (props.selectedLocation && planningItemIsOnLocation(groupedItems[0], props.selectedLocation)) {
            selectMarker(marker);
          }

          google.maps.event.addListener(marker, "click", () => {
            props.onMarkerClicked(groupedItems);
            bounce(marker);
            props.onSelectedLocationChange(groupedItems[0].point);
          });

          marker.content.addEventListener("mouseenter", () => {
            props.onHoveredLocationChange(groupedItems[0].point);
          });

          marker.content.addEventListener("mouseleave", () => {
            props.onHoveredLocationChange(undefined);
          });
        },
      }
    }
  });

  return <div className="google-map-planning-container">
    {isLoading && <div className="google-map-planning-overlay">
      <Loading size="large" inverted />
    </div>}
    <GoogleMap
      ref={mapRef}
      mapId="DEMO_MAP_ID"
      coordinates={coordinates}
      mapOptions={{
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        },
        zoom,
        center,
        zoomControlOptions: {
          style: google.maps.ZoomControlStyle.SMALL,
        },
        scrollwheel: true
      }}
      onLoaded={map => {
        google.maps.event.addListener(map, "mouseout", () => {
          map.setOptions({
            scrollwheel: false
          });
        });

        google.maps.event.addListener(map, "mouseover", () => {
          map.setOptions({
            scrollwheel: true
          });
        });

        google.maps.event.addListener(map, "bounds_changed", () => {
          props.onBoundsChange(map.getBounds());
          setCenter({ lat: map.getCenter().lat(), lng: map.getCenter().lng() });
        });

        google.maps.event.addListener(map, "zoom_changed", () => {
          setZoom(map.getZoom());
        });

        google.maps.event.addListener(map, "click", () => {
          props.onMarkerClicked(undefined);
          selectMarker(undefined);
        });
      }}
    />
  </div>
});